import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Link, useHistory, resolveRoute, prepareSearchQueryParams, useRouterState} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {etmTaskStatusTypes} from '@ace-de/eua-entity-types';
import {useStyles, Option} from '@ace-de/ui-components';
import {ContentBlock, ContentItem} from '@ace-de/ui-components/app-layout';
import {Tab, TabBar} from '@ace-de/ui-components/navigation';
import config from '../../config';
import routePaths from '../../routePaths';
import keycloakAuth from '../../keycloakAuth';
import dashboardScreenTabs from '../dashboardScreenTabs';
import EUALogo from './EUALogo';
import dashboardNavigationLayouts from '../dashboardNavigationLayouts';
import {ecsLayouts} from '../../application/ecsLayoutTypes';
import ecsAccessControl from '../../ecsAccessControl';
import AvatarMenu from './AvatarMenu';
import {ecsFeatureActions, ecsFeatures} from '../../application/ecsFeatures';
// import Notifications from './Notifications';

const NavigationBarContent = ({signOutUser, user, allowedShiftRoles}) => {
    const {cx} = useStyles();
    const history = useHistory();
    const {translate} = useTranslate();
    const {activeRoutePath} = useRouterState();
    const activeRoleLayout = ecsAccessControl.getRoleScreenLayout(ecsLayouts.DASHBOARD_NAVIGATION);
    const isTaskAccessAvailable = ecsAccessControl.grantFeatureAccess(ecsFeatures.TASK, ecsFeatureActions.READ);
    const isShiftRoleSelectionAvailable = ecsAccessControl.grantFeatureAccess(
        ecsFeatures.SHIFT_ROLE_SELECTION,
        ecsFeatureActions.UPDATE,
    );

    const handleOnSelect = value => {
        if (value === 'logout') {
            signOutUser({});
            return;
        }
        if (value === 'shiftRole') {
            history.push(routePaths.SHIFT_ROLE_SELECTION);
        }
    };

    const navigateToRoute = routePath => {
        if (!routePath) return;

        // implemented as a hotfix, see https://computerrock.atlassian.net/browse/ACEECS-6135
        // TODO: check if we can adjust SelectableGroup logic to avoid having
        // an unnecessary 'navigateToRoute' call when the route has been changed programmatically
        if (routePath === activeRoutePath) return;

        // OVERDUE & DUE filters need to be pre-selected
        if (routePath === routePaths.TASK_SEARCH && isTaskAccessAvailable) {
            history.push(resolveRoute(
                routePaths.TASK_SEARCH,
                {},
                {search: prepareSearchQueryParams({
                    status: [etmTaskStatusTypes.DUE, etmTaskStatusTypes.OVERDUE],
                })},
            ));
            return;
        }

        // other routes
        history.push(resolveRoute(routePath));
    };

    return (
        <ContentBlock
            className={cx([
                'ace-c-content-block',
                'global!ace-u-flex',
                'global!ace-u-flex--align-center',
            ])}
        >
            <ContentItem
                className={cx([
                    'ace-c-content-item--large-span-8',
                    'ace-c-content-item--x-large-span-5',
                    'global!ace-u-flex',
                    'global!ace-u-flex--align-center',
                ])}
            >
                <Link to={resolveRoute(routePaths.DASHBOARD)}>
                    <EUALogo />
                </Link>
                <TabBar
                    name="dashboardTabs"
                    value={activeRoutePath}
                    onChange={navigateToRoute}
                >
                    <Tab
                        name={dashboardScreenTabs.SERVICE_CASES}
                        value={routePaths.SERVICE_CASE_SEARCH}
                    >
                        {translate('navigation_bar.tab_name.cases')}
                    </Tab>
                    {!config.IS_FALLBACK_SYSTEM && (
                        <Tab
                            name={dashboardScreenTabs.TASKS}
                            value={routePaths.TASK_SEARCH}
                            isDisabled={!isTaskAccessAvailable}
                        >
                            {translate('navigation_bar.tab_name.tasks')}
                        </Tab>
                    )}
                    <Tab
                        name={dashboardScreenTabs.CONTRACT_PARTNERS}
                        value={routePaths.CONTRACT_PARTNER_SEARCH}
                    >
                        {translate('navigation_bar.tab_name.contract_partners')}
                    </Tab>
                </TabBar>
            </ContentItem>
            <ContentItem
                className={cx([
                    'ace-c-content-item--large-span-3',
                    'ace-c-content-item--x-large-span-6',
                    'global!ace-u-flex',
                    'global!ace-u-flex--justify-flex-end',
                ])}
            >
                {activeRoleLayout
                    ? dashboardNavigationLayouts[activeRoleLayout]
                    : null
                 }
            </ContentItem>
            <ContentItem
                className={cx([
                    'ace-c-content-item--span-1',
                    'global!ace-u-flex',
                    'global!ace-u-flex--justify-flex-end',
                    'global!ace-u-flex--align-center',
                ])}
            >
                {user && (
                    <AvatarMenu
                        userImage=""
                        userName={`${user?.initials}`}
                        className="ace-c-avatar--small"
                        onSelect={handleOnSelect}
                    >
                        {/*
                        <Option name="avatarOptionProfile" value="profile">
                            {translate('navigation_bar.avatar_menu_option.profile')}
                        </Option>
                        <Option name="avatarOptionSettings" value="settings">
                            {translate('navigation_bar.avatar_menu_option.settings')}
                        </Option>
                        */}
                        {isShiftRoleSelectionAvailable && allowedShiftRoles && allowedShiftRoles.length > 1 && (
                            <Option name="avatarOptionChangeRole" value="shiftRole">
                                {translate('navigation_bar.avatar_menu_option.change_role')}
                            </Option>
                        )}
                        <Option
                            name="avatarOptionLogout"
                            value="logout"
                        >
                            {translate('navigation_bar.avatar_menu_option.log_out')}
                        </Option>
                    </AvatarMenu>
                )}
            </ContentItem>
        </ContentBlock>
    );
};

NavigationBarContent.propTypes = {
    signOutUser: PropTypes.func,
    user: PropTypes.object,
    allowedShiftRoles: PropTypes.array,
};

NavigationBarContent.defaultProps = {
    signOutUser: () => null,
    user: null,
    allowedShiftRoles: null,
};

const mapStateToProps = state => ({
    user: state.user.user,
    allowedShiftRoles: state.user.allowedShiftRoles,
});

const mapDispatchToProps = dispatch => ({
    signOutUser: () => dispatch(keycloakAuth.signOutUserAction),
});

export default connect(mapStateToProps, mapDispatchToProps)(NavigationBarContent);
